/**
 * `/crm/deals/:id` — one deal: where it is in the pipeline, who it is with,
 * what happens next, and what happened so far.
 *
 * The stage bar across the top is the pipeline with this deal's place marked;
 * pressing a stage moves an open deal there, the same act as dragging its card
 * on the board.
 */
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Chip from '@mui/material/Chip';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import { CRM_PERMISSIONS, type DealDto, type StageDto } from '@forsys/shared';
import { useAuth } from '../../auth/useAuth';
import { AsyncContent } from '../../components/AsyncContent';
import { Columns } from '../../components/Columns';
import { Details } from '../../components/Details';
import { EntityDetail } from '../../components/EntityDetail';
import { Panel } from '../../components/Panel';
import { Timestamp } from '../../components/Timestamp';
import { useAsync } from '../../hooks/useAsync';
import { useSubmit } from '../../hooks/useSubmit';
import { useDealActions } from './actions';
import { ActivityPanel } from './ActivityPanel';
import { getDeal, listStages, moveDeal } from './api';
import { DealHistory } from './DealHistory';
import { DealStatusChip } from './DealStatus';
import { ConvertedValue } from './ConvertedValue';
import { formatDay, formatMoney } from './format';

function StageBar({
  deal,
  stages,
  pending,
  onMove,
}: {
  deal: DealDto;
  stages: readonly StageDto[];
  pending: boolean;
  onMove: (stageId: number) => void;
}) {
  const { t } = useTranslation('crm');
  const { can } = useAuth();
  const movable = deal.status === 'open' && can(CRM_PERMISSIONS.WRITE) && !pending;
  const at = stages.findIndex((stage) => stage.id === deal.stage.id);

  return (
    <Stack direction="row" spacing={0.5} sx={{ mb: 2, flexWrap: 'wrap', rowGap: 0.5 }} aria-label={t('deal.stageBar')}>
      {stages.map((stage, index) => (
        <Chip
          key={stage.id}
          label={stage.name}
          color={index <= at ? (deal.status === 'lost' ? 'default' : 'primary') : 'default'}
          variant={stage.id === deal.stage.id ? 'filled' : index < at ? 'filled' : 'outlined'}
          {...(movable && stage.id !== deal.stage.id ? { onClick: () => onMove(stage.id) } : {})}
          {...(stage.id === deal.stage.id ? { 'aria-current': 'step' as const } : {})}
          sx={{ borderRadius: 1 }}
        />
      ))}
    </Stack>
  );
}

export default function DealPage() {
  const { t } = useTranslation('crm');
  const navigate = useNavigate();
  const id = Number(useParams()['id']);
  const deal = useAsync(() => getDeal(id), [id]);
  const stages = useAsync(listStages, []);

  const actions = useDealActions({
    onChanged: (key) => (key === 'delete' ? navigate('/crm/deals') : deal.reload()),
  });
  const move = useSubmit(async (stageId: number) => {
    const result = await moveDeal(id, stageId);
    deal.setData(result);
    return result;
  });

  return (
    <AsyncContent state={deal}>
      {({ deal: record }) => (
        <EntityDetail
          backTo="/crm/deals"
          backLabel={t('deal.back')}
          title={record.title}
          badges={
            <>
              {record.status !== 'open' && <DealStatusChip status={record.status} />}
              {record.rotten && <Chip size="small" color="error" variant="outlined" label={t('deal.rotten')} />}
            </>
          }
          description={formatMoney(record.value, record.currency)}
          entity={actions.forRow(record)}
          error={actions.error ?? move.error}
        >
          {stages.data && (
            <StageBar deal={record} stages={stages.data.stages} pending={move.pending} onMove={(stageId) => void move.submit(stageId)} />
          )}

          <Columns split={[1, 1.4]} align="start">
            <Stack spacing={3}>
              <Panel>
                <Details
                  rows={[
                    [
                      t('field.value'),
                      <span key="value">
                        {formatMoney(record.value, record.currency)}
                        <ConvertedValue converted={record.converted} />
                      </span>,
                    ],
                    [t('field.stage'), record.stage.name],
                    [
                      t('field.organization'),
                      record.organization && (
                        <Link component={RouterLink} to={`/crm/organizations/${record.organization.id}`} underline="hover">
                          {record.organization.name}
                        </Link>
                      ),
                    ],
                    [
                      t('field.person'),
                      record.person && (
                        <Link component={RouterLink} to={`/crm/persons/${record.person.id}`} underline="hover">
                          {record.person.name}
                        </Link>
                      ),
                    ],
                    [t('field.expectedCloseDate'), formatDay(record.expectedCloseDate)],
                    [t('field.owner'), record.owner.name],
                    ...(record.lostReason ? [[t('field.lostReason'), record.lostReason] as const] : []),
                    [t('deal.inStageSince'), <Timestamp key="since" value={record.stageChangedAt} />],
                    ...(record.closedAt ? [[t('deal.closedAt'), <Timestamp key="closed" value={record.closedAt} />] as const] : []),
                  ]}
                />
              </Panel>
              <DealHistory dealId={record.id} version={record.updatedAt} />
            </Stack>

            <ActivityPanel
              filter={{ dealId: record.id }}
              link={{ deal: { id: record.id, name: record.title } }}
              onChanged={deal.reload}
            />
          </Columns>
        </EntityDetail>
      )}
    </AsyncContent>
  );
}
